import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Divider, Form, Input, Modal, Row, Select, Space } from "antd";


import Notify from "../../../utils/notification-handler";
import CommonService from "../../../services/CommonService";
import DefaultDataHandler from "../../../utils/default-data-handler";
import { IRole } from "../../../types/Role.types";
import { IDirectoryData, IDirectoryDataUpdateModel } from "../../../types/DirectoryData.types";

import { TRANSLATION_KEY } from "../../../constants/commonConstants";
import { DIRECTORY_DATA_DATATABLE_SETTINGS } from "../../../constants/directoryDataConstants";
import { DATA_TABLE_HIDE_UPDATE_MODAL, DATA_TABLE_RELOAD, DATA_TABLE_SHOW_UPDATE_MODAL } from "../../../constants/pubSubKeys";



export interface IDirectoryDataUpdateModalProps {
    rolesLookups: IRole[];
}

export const DirectoryDataUpdateModal: React.FC<IDirectoryDataUpdateModalProps> = ({ rolesLookups }) => {

    const { t } = useTranslation([TRANSLATION_KEY.toString()]);
    const [form] = Form.useForm<IDirectoryData>();
    const [isShowModal, setIsShowModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updatingFormModel, setUpdatingFormModel] = useState<IDirectoryDataUpdateModel | null>(null);


    const closeUpdateModal = () => {
        form.resetFields();
        setUpdatingFormModel(null);
        setIsShowModal(false);
    }


    useEffect(() => {
        let showModalPubSub = PubSub.subscribe(DATA_TABLE_SHOW_UPDATE_MODAL, async (msg: string, updatingItem: IDirectoryData | null) => {

            let updatingItemModel: IDirectoryDataUpdateModel;

            if (updatingItem === null) {
                updatingItemModel = DefaultDataHandler.newDirectoryDataUpdateModel();
            }
            else {
                updatingItemModel = {
                    ...updatingItem,
                    roleNames: updatingItem.roles?.split(',')
                };
            }

            form.setFieldsValue(updatingItemModel);
            setUpdatingFormModel(updatingItemModel);
            setIsShowModal(true);

        });

        let hideModalPubSub = PubSub.subscribe(DATA_TABLE_HIDE_UPDATE_MODAL, () => {
            closeUpdateModal();
        })

        return () => {
            PubSub.unsubscribe(showModalPubSub);
            PubSub.unsubscribe(hideModalPubSub);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onFinish = (updatingItem: any) => {
        if (!updatingFormModel) {
            return;
        }

        updatingItem.id = updatingFormModel.id;
        updatingItem.roles = updatingItem.roleNames.join();
        delete updatingItem.roleNames;

        edit(updatingItem);
    };


    const edit = async (updatingItem: IDirectoryData) => {
        setIsLoading(true);

        let url = `data/${DIRECTORY_DATA_DATATABLE_SETTINGS.dataEndpoint}/${updatingItem.id}`
        const odataResponse = await CommonService.patch(url, updatingItem);

        if (odataResponse === 1 || odataResponse === 0) {
            Notify.success(t('common-itemUpdatedSuccessfully'));
        } 

        PubSub.publish(DATA_TABLE_RELOAD);
        closeUpdateModal();
        setIsLoading(false);
    }

    return (
        <>
            {
                updatingFormModel &&
                <Modal
                    title={(updatingFormModel.id !== 0) ? t('resources-directoryData-directoryDataEditModalTitle') : t('resources-directoryData-directoryDataAddModalTitle')}
                    className="data-table-item-update-modal"
                    visible={isShowModal}
                    onCancel={closeUpdateModal} footer={null}>

                    <Form form={form}
                        name="data-table-item-update-form"
                        layout="vertical"
                        onFinish={onFinish}>

                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item
                                    name="title"
                                    label={t('directoryData-title')}
                                    required={true}
                                    rules={[{ required: true, message: `${t('common-validation-requiredValidationError')}` }]}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name="path"
                                    label={t('directoryData-path')}>
                                    <Input disabled={true} />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name={['roleNames']}
                                    label={t('directoryData-roles')}
                                    required={true}
                                    rules={[{ required: true, message: `${t('common-validation-requiredValidationError')}` }]}>
                                    <Select mode="multiple" allowClear>
                                        {
                                            rolesLookups.map((userRolesLookup: IRole) => {
                                                return (<Select.Option value={userRolesLookup.name}>{userRolesLookup.name}</Select.Option>)
                                            })
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>

                        </Row>

                        <Divider />

                        <Row justify="end">
                            <Col span={24}>
                                <Space className="data-table-item-update-modal-footer">
                                    <Button onClick={closeUpdateModal}>
                                        {t('common-cancel')}
                                    </Button>
                                    <Button type="primary" htmlType="submit" loading={isLoading}>
                                        {t('common-save')}
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>

                </Modal>
            }
        </>
    )
}